import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../common/Button.js';

const UNLOAD_NOTE_OPTIONS = [
  'Parcel in open condition',
  'Damaged parcel',
  'Parcel shortage / missing items',
];

const COPY = {
  unload: {
    heading: 'Unload shipments',
    body: 'Unload this GC at the destination hub. The remark below is sent to the booking agent. You can edit it.',
    label: 'Unload',
    placeholder: 'Enter note for the booking agent…',
    defaultNote: 'Unloaded at destination hub',
    requireNote: true,
    variant: 'primary',
  },
  revert: {
    heading: 'Revert unload',
    body: 'This GC goes back to pending unload (loaded). You can unload it again. It stays on this ITCL request.',
    label: 'Revert',
    placeholder: 'Optional note…',
    requireNote: false,
    variant: 'danger',
  },
  remove: {
    heading: 'Remove from ITCL request',
    body: 'This GC leaves this ITCL request and goes back to the booking agent as Booked. They get your note.',
    label: 'Remove',
    placeholder: 'Enter note for the booking agent…',
    requireNote: true,
    variant: 'danger',
  },
  note: {
    heading: 'Add note',
    body: 'Select a reason. It is sent to the booking agent. Unload status does not change.',
    label: 'Save note',
    placeholder: 'Select a reason',
    requireNote: true,
    reasonOptions: UNLOAD_NOTE_OPTIONS,
    variant: 'primary',
  },
};

/**
 * Note / confirm modal for ITCL unload, revert-unload, and remove-from-request.
 */
const ItclActionNoteModal = ({
  open,
  action = 'unload',
  title,
  description,
  confirmLabel,
  shipmentCount = 0,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [note, setNote] = useState('');
  const copy = COPY[action] || COPY.unload;

  useEffect(() => {
    if (open) setNote(copy.defaultNote || '');
  }, [open, action, copy.defaultNote]);

  if (!open) return null;

  const heading = title || copy.heading;
  const body = description || copy.body;
  const label = confirmLabel || copy.label;
  const trimmed = note.trim();
  const noteOk = !copy.requireNote || Boolean(trimmed);

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => !loading && onClose?.()}
      role="presentation"
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-2xl shadow-xl p-4 sm:p-5 max-h-[90vh] overflow-y-auto min-w-0"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="itcl-action-note-title"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 id="itcl-action-note-title" className="text-base sm:text-lg font-semibold text-secondary">
              {heading}
            </h3>
            {shipmentCount > 0 ? (
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                {shipmentCount} shipment{shipmentCount === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-3">{body}</p>

        <label className="block text-sm font-medium text-secondary mb-1.5">
          {copy.reasonOptions ? 'Reason' : 'Note'}{' '}
          {copy.requireNote ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(optional)</span>}
        </label>
        {copy.reasonOptions ? (
          <select
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="">{copy.placeholder}</option>
            {copy.reasonOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            disabled={loading}
            placeholder={copy.placeholder}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm resize-y min-h-[88px] focus:outline-none focus:border-blue-400"
          />
        )}
        {copy.requireNote && !trimmed ? (
          <p className="mt-1 text-xs text-red-600">
            {copy.reasonOptions ? 'Select a reason' : 'Note is required'}
          </p>
        ) : null}

        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            className="w-full sm:w-auto"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={copy.variant}
            loading={loading}
            disabled={loading || !noteOk}
            className="w-full sm:w-auto"
            onClick={() => onConfirm?.(trimmed)}
          >
            {label}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ItclActionNoteModal;
